import React, { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { formatPriceWithCommas, isoToLocalDateTime } from "../utils/stringUtils";

import {
  Box,
  Grid,
  Typography,
  Tooltip,
  Divider,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";

// Direction icons
import OutIcon from '@mui/icons-material/ArrowBack';
import InIcon from '@mui/icons-material/ArrowForward';

// More info tooltip icon
import MoreInfoIcon from '@mui/icons-material/Info';

// Action icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import { useApis } from "../../api/useApiClient";
import { useNotification } from "../../context/NotificationContext";

import DetailsView from "../Common/Views/DetailsView";
import PaymentAllocationsList from "./PaymentAllocationsList";

import PaymentStatusChip from "./PaymentStatusChip";


const displayDirection = (direction) => {
  if (direction === 'IN') {
    return <><InIcon fontSize="small" style={{ verticalAlign: 'middle', color: 'green' }} /> IN</>;
  }
  if (direction === 'OUT') {
    return <><OutIcon fontSize="small" style={{ verticalAlign: 'middle', color: 'red' }} /> OUT</>;
  }
  return 'N/A';
}

const displayDueAt = (dueAt, status) => {
  if (!dueAt) return <Typography variant="body2">Not available</Typography>;

  const dueDate = new Date(dueAt);
  const now = new Date();

  if (status !== 'SETTLED' && dueDate < now) {
    return <Typography variant="body2" color="error">{isoToLocalDateTime(dueAt)} (Overdue)</Typography>;
  }
  return <Typography variant="body2">{isoToLocalDateTime(dueAt)}</Typography>;
}

const displayAllocationPercentage = (totalAmount, allocatedAmount) => {

  if (!totalAmount || !allocatedAmount) return "-";
  const percentage = ((allocatedAmount / totalAmount) * 100).toFixed(2);

  let color = 'green';

  if (percentage < 100) {
    color = 'orange';
  }
  if (percentage > 100) {
    color = 'red';
  }

  return <Typography variant="body2" color={color} fontWeight='bold'>{percentage}%</Typography>;
}

function PaymentDataLayout({ data }) {
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={4}>
          <Typography variant="subtitle2" color="text.secondary">
            Direction
          </Typography>
          <Typography variant="body2">{displayDirection(data.direction)}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <Typography variant="subtitle2" color="text.secondary">
            Payment method
          </Typography>
          <Typography variant="body2">{data.method || "-"}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <Typography variant="subtitle2" color="text.secondary">
            Amount
          </Typography>
          <Typography variant="body2" fontWeight="bold">{formatPriceWithCommas(data.amount)} {data.currency || ""}</Typography>
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <Typography variant="subtitle2" color="text.secondary">
            External Reference ID
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {data.externalReferenceId || "-"}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <Typography variant="subtitle2" color="text.secondary">
            Reference document
          </Typography>
          <Typography variant="body2">{data.referenceDocument || "-"}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <Typography variant="subtitle2" color="text.secondary">
            Status
          </Typography>
          <PaymentStatusChip status={data.status} />
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <Typography variant="subtitle2" color="text.secondary">
            Created at
            <Tooltip title={isoToLocalDateTime(data.createdAt, { date: true, time: true, seconds: true })} arrow>
              <MoreInfoIcon fontSize="small" style={{ verticalAlign: 'middle', marginLeft: 4 }} />
            </Tooltip>
          </Typography>
          <Typography variant="body2">{isoToLocalDateTime(data.createdAt)}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <Typography variant="subtitle2" color="text.secondary">
            Submitted at
            <Tooltip title={isoToLocalDateTime(data.submittedAt, { date: true, time: true, seconds: true })} arrow>
              <MoreInfoIcon fontSize="small" style={{ verticalAlign: 'middle', marginLeft: 4 }} />
            </Tooltip>
          </Typography>
          <Typography variant="body2">{isoToLocalDateTime(data.submittedAt)}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <Typography variant="subtitle2" color="text.secondary">
            Payment due on
          </Typography>
          <Typography variant="body2">{displayDueAt(data.dueAt, data.status)}</Typography>
        </Grid>

        {data.settledAt &&
          <Grid item xs={12} sm={6} lg={4}>
            <Typography variant="subtitle2" color="text.secondary">
              Settled at
              <Tooltip title={isoToLocalDateTime(data.settledAt, { date: true, time: true, seconds: true })} arrow>
                <MoreInfoIcon fontSize="small" style={{ verticalAlign: 'middle', marginLeft: 4 }} />
              </Tooltip>
            </Typography>
            <Typography variant="body2">{isoToLocalDateTime(data.settledAt)}</Typography>
          </Grid>
        }

        {data.failedAt &&
          <Grid item xs={12} sm={6} lg={4}>
            <Typography variant="subtitle2" color="text.secondary">
              Failed at
              <Tooltip title={isoToLocalDateTime(data.failedAt, { date: true, time: true, seconds: true })} arrow>
                <MoreInfoIcon fontSize="small" style={{ verticalAlign: 'middle', marginLeft: 4 }} />
              </Tooltip>
            </Typography>
            <Typography variant="body2">{isoToLocalDateTime(data.failedAt)}</Typography>
          </Grid>
        }

        {data.recievedAt &&
          <Grid item xs={12} sm={6} lg={4}>
            <Typography variant="subtitle2" color="text.secondary">
              Received at
              <Tooltip title={isoToLocalDateTime(data.recievedAt, { date: true, time: true, seconds: true })} arrow>
                <MoreInfoIcon fontSize="small" style={{ verticalAlign: 'middle', marginLeft: 4 }} />
              </Tooltip>
            </Typography>
            <Typography variant="body2">{isoToLocalDateTime(data.recievedAt)}</Typography>
          </Grid>
        }

      </Grid>

      {/* <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={4}>
          <Typography variant="subtitle2" color="text.secondary">
            Total allocated amount
          </Typography>
          <Typography variant="body2">{formatPriceWithCommas(data.totalAllocatedAmount)} {data.currency || ""}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <Typography variant="subtitle2" color="text.secondary">
            Unallocated amount
          </Typography>
          <Typography variant="body2">{formatPriceWithCommas(data.unallocatedAmount)} {data.currency || ""}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <Typography variant="subtitle2" color="text.secondary">
            Allocation percentage
          </Typography>
          {displayAllocationPercentage(data.amount, data.totalAllocatedAmount)}
        </Grid>
      </Grid> */}

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary">
            Notes
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {data.notes || "-"}
          </Typography>
        </Grid>
      </Grid>
    </>
  );
}


export default function PaymentDetails() {
  const { id } = useParams();
  const { tenantApi } = useApis();
  const { showNotification } = useNotification();

  // Ref to hold the reload function from DetailsView children
  const reloadRef = useRef(null);

  // Dialog state
  const [settleDialogOpen, setSettleDialogOpen] = useState(false);
  const [failDialogOpen, setFailDialogOpen] = useState(false);

  // Form state
  const [settleNotes, setSettleNotes] = useState('');
  const [settleReceivedAt, setSettleReceivedAt] = useState('');
  const [failReason, setFailReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Handle settle payment
  async function handleSettlePayment() {
    setActionLoading(true);
    try {
      const body = {};
      if (settleNotes.trim()) body.notes = settleNotes;
      if (settleReceivedAt) body.receivedAt = new Date(settleReceivedAt).toISOString();

      await tenantApi(`/tenants/payments/${id}/settle`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      showNotification('Payment settled successfully', 'success');
      setSettleDialogOpen(false);
      setSettleNotes('');
      setSettleReceivedAt('');
      if (reloadRef.current) reloadRef.current();
    } catch (e) {
      console.error(e);
      showNotification(e.message || 'Failed to settle payment', 'error');
    } finally {
      setActionLoading(false);
    }
  }

  // Handle fail payment
  async function handleFailPayment() {
    if (!failReason.trim()) {
      showNotification('Please provide a reason for marking the payment as failed', 'warning');
      return;
    }

    setActionLoading(true);
    try {
      await tenantApi(`/tenants/payments/${id}/fail`, {
        method: 'PATCH',
        body: JSON.stringify({ reason: failReason }),
      });
      showNotification('Payment marked as failed', 'success');
      setFailDialogOpen(false);
      setFailReason('');
      if (reloadRef.current) reloadRef.current();
    } catch (e) {
      console.error(e);
      showNotification(e.message || 'Failed to update payment status', 'error');
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <>
      <DetailsView
        title="Payment details"
        // subtitleAttribute="name"
        apiPath={`/tenants/payments/${id}`}
        // editPath={`/payments/${id}/edit`}
        onDelete={(data) => {
          console.log("Delete action for", data);
          // you can plug API delete call here
        }}
      >
        {({ data, reload }) => {
          // Store reload in ref so handlers can access it
          reloadRef.current = reload;
          return (
            <>
              <PaymentDataLayout data={data} />

              {data.status === 'PENDING' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" justifyContent="flex-end" spacing={2}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => setSettleDialogOpen(true)}
                    >
                      Mark as Settled
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => setFailDialogOpen(true)}
                    >
                      Mark as Failed
                    </Button>
                  </Stack>
                </>
              )}
            </>
          );
        }}
      </DetailsView>

      <Box margin={2} mt={4} padding={2}>
        <PaymentAllocationsList
          filters={{ paymentId: id }}
          // addButton={false}
          columns={['payableType', 'payableId', 'amount', 'status', 'createdAt']}
          searchBox={false}
          noResultsMessage="No payment allocations found for this payment"
        />
      </Box>

      {/* Settle Payment Dialog */}
      <Dialog open={settleDialogOpen} onClose={() => setSettleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Settle Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to mark this payment as settled?
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Notes (optional)"
            fullWidth
            multiline
            rows={3}
            value={settleNotes}
            onChange={(e) => setSettleNotes(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Received at"
            type="datetime-local"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={settleReceivedAt}
            onChange={(e) => setSettleReceivedAt(e.target.value)}
            helperText="Leave blank to use current time"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSettleDialogOpen(false); setSettleNotes(''); setSettleReceivedAt(''); }}>
            Cancel
          </Button>
          <Button
            onClick={handleSettlePayment}
            color="success"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? 'Settling...' : 'Settle Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fail Payment Dialog */}
      <Dialog open={failDialogOpen} onClose={() => setFailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Fail Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to mark this payment as failed? This action cannot be undone.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for failure"
            fullWidth
            multiline
            rows={3}
            value={failReason}
            onChange={(e) => setFailReason(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setFailDialogOpen(false); setFailReason(''); }}>
            Cancel
          </Button>
          <Button
            onClick={handleFailPayment}
            color="error"
            variant="contained"
            disabled={actionLoading || !failReason.trim()}
          >
            {actionLoading ? 'Processing...' : 'Mark as Failed'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
};